/* eslint-disable no-console */

import { Vector3 } from '@babylonjs/core/Maths/math';
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import { IndicesArray, Nullable } from '@babylonjs/core/types';
import { KbGeometry } from '@kb3d/models/classes';
import { SubmeshData } from '@kb3d/models/classes/features/submesh-feature';

export interface ISubmeshVertexData {
    positions?: Nullable<Float32Array>;
    normals?: Nullable<Float32Array>;
    tangents?: Nullable<Float32Array>;
    uvs?: Nullable<Float32Array>;
    uvs2?: Nullable<Float32Array>;
    uvs3?: Nullable<Float32Array>;
    uvs4?: Nullable<Float32Array>;
    uvs5?: Nullable<Float32Array>;
    uvs6?: Nullable<Float32Array>;
    colors?: Nullable<Float32Array>;
    indices?: Nullable<Int32Array | Uint16Array>;
    paths?: Nullable<Int32Array | Uint16Array>;
    holes?: Nullable<Int32Array | Uint16Array>;
    submeshData?: Nullable<SubmeshData[]>;

    // NEW: Flag to preserve custom normals (for hard-edge features like Chamfer)
    preserveNormals?: boolean;
}

export class SubmeshVertexData extends VertexData {
    /** Array of linked index segments, i.e. 0,1,1,2,2,3. A break in the link corresponds to a new path. */
    paths?: Nullable<IndicesArray>;
    /** Array of pairs of numbers, the first is the hole identifier, and the second is the index at which the hole starts in the path array */
    holes?: Nullable<IndicesArray>;
    submeshData?: Nullable<SubmeshData[]>;

    // NEW: Flag to indicate normals should be preserved
    preserveNormals?: boolean;

    constructor() {
        super();
        this.indices = null;
        this.positions = null;
        this.normals = null;
        this.tangents = null;
        this.submeshData = null;
        this.preserveNormals = false; // Default: allow Babylon.js to recalculate
    }

    static CreateFromData(data: ISubmeshVertexData) {
        const result = new SubmeshVertexData();
        result.positions = data.positions || null;
        result.normals = data.normals || null;
        result.tangents = data.tangents || null;
        result.uvs = data.uvs || null;
        result.uvs2 = data.uvs2 || null;
        result.uvs3 = data.uvs3 || null;
        result.uvs4 = data.uvs4 || null;
        result.uvs5 = data.uvs5 || null;
        result.uvs6 = data.uvs6 || null;
        result.colors = data.colors || null;
        result.indices = data.indices || null;
        result.paths = data.paths || null;
        result.holes = data.holes || null;
        result.submeshData = data.submeshData || null;
        result.preserveNormals = data.preserveNormals || false; // NEW

        return result;
    }

    /**
     * @param kbGeometry the kbGeometry to copy the vertex data from.
     * Note that this copipes the vertex data by reference, so mutating the data WILL modify the kbGeometry.
     */
    static FromGeometry(kbGeometry: KbGeometry) {
        const result = new SubmeshVertexData();
        result.indices = kbGeometry.indices || null;
        result.positions = kbGeometry.positions || null;
        result.normals = kbGeometry.normals || null;
        result.tangents = kbGeometry.tangents || null;
        result.uvs = kbGeometry.uvs || null;
        result.colors = kbGeometry.colors || null;
        result.submeshData = [
            {
                materialIndex: 0,
                verticesStart: 0,
                verticesCount: kbGeometry.positions ? kbGeometry.positions.length / 3 : 0,
                indexStart: 0,
                indexCount: kbGeometry.indices ? kbGeometry.indices.length : 0,
            },
        ];

        return result;
    }

    static ExtractFromMesh(mesh: Mesh, copyWhenShared?: boolean | undefined, forceCopy?: boolean | undefined) {
        const result = new SubmeshVertexData();
        const vertexData = super.ExtractFromMesh(mesh, copyWhenShared, forceCopy);

        SubmeshVertexData.CopyToRef(vertexData, result);
        if (mesh.subMeshes) {
            result.submeshData = extractSubmeshData(mesh.subMeshes);
        }
        if (mesh.metadata && mesh.metadata['paths']) {
            result.paths = copyWhenShared ? mesh.metadata['paths'].slice() : mesh.metadata['paths'];
        }
        if (mesh.metadata && mesh.metadata['holes']) {
            result.holes = copyWhenShared ? mesh.metadata['holes'].slice() : mesh.metadata['holes'];
        }

        return result;
    }

    static CopyToRef(source: Partial<VertexData | SubmeshVertexData>, target: SubmeshVertexData) {
        target.positions = source.positions || null;
        if ('paths' in source) {
            target.paths = source.paths || null;
        }
        if ('holes' in source) {
            target.holes = source.holes || null;
        }
        target.normals = source.normals || null;
        target.tangents = source.tangents || null;
        target.uvs = source.uvs || null;
        target.uvs2 = source.uvs2 || null;
        target.uvs3 = source.uvs3 || null;
        target.uvs4 = source.uvs4 || null;
        target.uvs5 = source.uvs5 || null;
        target.uvs6 = source.uvs6 || null;
        target.colors = source.colors || null;
        target.matricesIndices = source.matricesIndices || null;
        target.matricesWeights = source.matricesWeights || null;
        target.matricesIndicesExtra = source.matricesIndicesExtra || null;
        target.matricesWeightsExtra = source.matricesWeightsExtra || null;
        target.indices = source.indices || null;
        target.submeshData = (source as SubmeshVertexData).submeshData;
    }

    static ExtendToRef(source: Partial<VertexData | SubmeshVertexData>, target: SubmeshVertexData) {
        if (source.positions !== undefined) target.positions = source.positions;
        if ('paths' in source && source.paths !== undefined) target.paths = source.paths;
        if ('holes' in source && source.holes !== undefined) target.holes = source.holes;
        if (source.normals !== undefined) target.normals = source.normals;
        if (source.tangents !== undefined) target.tangents = source.tangents;
        if (source.uvs !== undefined) target.uvs = source.uvs;
        if (source.uvs2 !== undefined) target.uvs2 = source.uvs2;
        if (source.uvs3 !== undefined) target.uvs3 = source.uvs3;
        if (source.uvs4 !== undefined) target.uvs4 = source.uvs4;
        if (source.uvs5 !== undefined) target.uvs5 = source.uvs5;
        if (source.uvs6 !== undefined) target.uvs6 = source.uvs6;
        if (source.colors !== undefined) target.colors = source.colors;
        if (source.matricesIndices !== undefined) target.matricesIndices = source.matricesIndices;
        if (source.matricesWeights !== undefined) target.matricesWeights = source.matricesWeights;
        if (source.matricesIndicesExtra !== undefined) target.matricesIndicesExtra = source.matricesIndicesExtra;
        if (source.matricesWeightsExtra !== undefined) target.matricesWeightsExtra = source.matricesWeightsExtra;
        if (source.indices !== undefined) target.indices = source.indices;
        if ((source as SubmeshVertexData).submeshData !== undefined) {
            target.submeshData = (source as SubmeshVertexData).submeshData;
        }
        // NEW: Preserve custom normals if the flag is set in the source
        if ((source as SubmeshVertexData).preserveNormals !== undefined) {
            target.preserveNormals = (source as SubmeshVertexData).preserveNormals;
        }
    }

    toTransferrable() {
        const vertexData: ISubmeshVertexData = {
            positions: this.positions ? new Float32Array(this.positions) : null,
            normals: this.normals ? new Float32Array(this.normals) : null,
            tangents: this.tangents ? new Float32Array(this.tangents) : null,
            uvs: this.uvs ? new Float32Array(this.uvs) : null,
            uvs2: this.uvs2 ? new Float32Array(this.uvs2) : null,
            uvs3: this.uvs3 ? new Float32Array(this.uvs3) : null,
            uvs4: this.uvs4 ? new Float32Array(this.uvs4) : null,
            uvs5: this.uvs5 ? new Float32Array(this.uvs5) : null,
            uvs6: this.uvs6 ? new Float32Array(this.uvs6) : null,
            colors: this.colors ? new Float32Array(this.colors) : null,
            indices: this.indices ? new Int32Array(this.indices) : null,
            paths: this.paths ? new Int32Array(this.paths) : null,
            holes: this.holes ? new Int32Array(this.holes) : null,
            submeshData: this.submeshData ? JSON.parse(JSON.stringify(this.submeshData)) : undefined,
            preserveNormals: this.preserveNormals, // NEW
        };
        const transfer = [
            vertexData.positions?.buffer,
            vertexData.normals?.buffer,
            vertexData.tangents?.buffer,
            vertexData.uvs?.buffer,
            vertexData.uvs2?.buffer,
            vertexData.uvs3?.buffer,
            vertexData.uvs4?.buffer,
            vertexData.uvs5?.buffer,
            vertexData.uvs6?.buffer,
            vertexData.colors?.buffer,
            vertexData.indices?.buffer,
            vertexData.paths?.buffer,
            vertexData.holes?.buffer,
        ].filter(buffer => !!buffer) as ArrayBuffer[];
        return { vertexData, transfer };
    }

    deepClone() {
        const result = new SubmeshVertexData();
        result.indices = this.indices ? this.indices.slice() : null;
        result.positions = this.positions ? this.positions.slice() : null;
        result.paths = this.paths ? this.paths.slice() : null;
        result.holes = this.holes ? this.holes.slice() : null;
        result.normals = this.normals ? this.normals.slice() : null;
        result.tangents = this.tangents ? this.tangents.slice() : null;
        result.uvs = this.uvs ? this.uvs.slice() : null;
        result.colors = this.colors ? this.colors.slice() : null;
        result.submeshData = this.submeshData ? JSON.parse(JSON.stringify(this.submeshData)) : undefined;

        return result;
    }

    shallowClone() {
        const result = new SubmeshVertexData();
        SubmeshVertexData.CopyToRef(this, result);
        return result;
    }

    copyFrom(source: Partial<SubmeshVertexData>) {
        SubmeshVertexData.CopyToRef(source, this);
    }

    merge(
        others: SubmeshVertexData | SubmeshVertexData[],
        use32BitsIndices?: boolean,
        forceCloneIndices?: boolean,
        mergeMaterialIds?: boolean,
        enableCompletion?: boolean
    ): SubmeshVertexData {
        if (!Array.isArray(others)) {
            others = [others];
        }

        let pathIdxOffset = this.paths ? this.paths.length : 0;
        let segmentIdxOffset = 0;
        let vertexIdxOffset = this.positions ? this.positions.length / 3 : 0;
        const newPaths: number[] = [];
        const newHoles: number[] = [];

        if (this.paths) {
            for (let i = 2; i < this.paths.length; i += 2) {
                if (this.paths[i] !== this.paths[i - 1]) {
                    segmentIdxOffset++;
                }
            }
            if (this.holes) {
                segmentIdxOffset -= this.holes.length / 2;
            }
            segmentIdxOffset++;
        }

        others.map(other => {
            if (other.holes) {
                for (let i = 0; i < other.holes.length; i += 2) {
                    newHoles.push(other.holes[i] + segmentIdxOffset, other.holes[i + 1] + pathIdxOffset);
                }
                segmentIdxOffset -= other.holes.length / 2;
            }
            if (other.paths) {
                for (let i = 0; i < other.paths.length; i++) {
                    newPaths.push(other.paths[i] + vertexIdxOffset);
                }
                for (let i = 2; i < other.paths.length; i += 2) {
                    if (other.paths[i] !== other.paths[i - 1]) {
                        segmentIdxOffset++;
                    }
                }
                segmentIdxOffset++;
            }

            vertexIdxOffset += other.positions ? other.positions.length / 3 : 0;
            pathIdxOffset += other.paths ? other.paths.length : 0;
        });

        super.merge(others, use32BitsIndices, true, mergeMaterialIds, enableCompletion);

        if (newHoles.length) {
            if (!this.holes) {
                this.holes = new Int32Array();
            }
            if ('push' in this.holes) {
                for (let i = 0; i < newHoles.length; i++) {
                    this.holes.push(newHoles[i]);
                }
            } else {
                const newArr: Int32Array = new Int32Array(this.holes.length + newHoles.length);
                newArr.set(this.holes);
                newArr.set(newHoles, this.holes.length);
                this.holes = newArr;
            }
        }
        if (newPaths.length) {
            if (!this.paths) {
                this.paths = new Int32Array();
            }
            if ('push' in this.paths) {
                for (let i = 0; i < newPaths.length; i++) {
                    this.paths.push(newPaths[i]);
                }
            } else {
                const newArr: Int32Array = new Int32Array(this.paths.length + newPaths.length);
                newArr.set(this.paths);
                newArr.set(newPaths, this.paths.length);
                this.paths = newArr;
            }
        }
        return this;
    }

    applyToMesh(mesh: Mesh, updatable = true, writeSubmeshes = true) {
        if (!this.submeshData && writeSubmeshes && mesh.subMeshes && mesh.subMeshes.length > 1) {
            this.submeshData = extractSubmeshData(mesh.subMeshes);
        }
        
        // NUEVO: Si preserveNormals est� activado, TEMPORALMENTE eliminar normals
        // para que super.applyToMesh() NO las recalcule
        let savedNormals: Float32Array | null = null;

        if (this.preserveNormals && this.normals) {
            savedNormals = new Float32Array(this.normals);
            // do not delete this.normals
        }

        
        const result = super.applyToMesh(mesh, updatable);
        
        // RESTAURAR las normales personalizadas DESPU�S
        if (savedNormals && mesh.geometry) {
            try {
                mesh.updateVerticesData(VertexBuffer.NormalKind, savedNormals, updatable, false);
                console.log('[GeometryHelper] Restored', savedNormals.length / 3, 'custom normals (preserveNormals=true)');
            } catch (e) {
                console.warn('Failed to restore custom normals:', e);
            }
            // Restaurar el valor original en this.normals
            this.normals = savedNormals;
        }
        
        if (this.paths) {
            if (!mesh.metadata) {
                mesh.metadata = {};
            }
            mesh.metadata['paths'] = this.paths.slice();
        }
        if (this.holes) {
            if (!mesh.metadata) {
                mesh.metadata = {};
            }
            mesh.metadata['holes'] = this.holes.slice();
        }
        if (mesh.geometry) {
            if (!this.normals && !savedNormals) { // <- MODIFICADO
                mesh.geometry.removeVerticesData(VertexBuffer.NormalKind);
            }
            if (!this.uvs) {
                mesh.geometry.removeVerticesData(VertexBuffer.UVKind);
            }
            if (!this.colors) {
                mesh.geometry.removeVerticesData(VertexBuffer.ColorKind);
            }
        }

        if (writeSubmeshes) {
            if (this.submeshData) {
                mesh.subMeshes = [];
                this.submeshData.forEach(submesh => {
                    // SubMeshes add themselves to the mesh
                    new SubMesh(
                        submesh.materialIndex,
                        submesh.verticesStart,
                        submesh.verticesCount,
                        submesh.indexStart,
                        submesh.indexCount,
                        mesh
                    );
                });
            }
        } else {
            this.submeshData = extractSubmeshData(mesh.subMeshes);
        }

        return result;
    }
}

function extractSubmeshData(subMeshes: SubMesh[]): SubmeshData[] {
    return subMeshes.map(submesh => {
        return {
            materialIndex: submesh.materialIndex,
            verticesStart: submesh.verticesStart,
            verticesCount: submesh.verticesCount,
            indexStart: submesh.indexStart,
            indexCount: submesh.indexCount,
        };
    });
}

/**
 * @param vertexData
 * @returns null if geometry is valid, string if invalid
 */
export function verifyGeometry(vertexData: SubmeshVertexData): null | 'vertices' | 'submeshes' {
    let outputError: null | 'vertices' | 'submeshes' = null;
    if (vertexData.indices && vertexData.positions && vertexData.indices.length > 0) {
        const indicesCount = vertexData.indices.length;
        const vertexCount = vertexData.positions.length / 3;

        if (vertexData.submeshData) {
            vertexData.submeshData.forEach(submesh => {
                if (
                    submesh.indexStart + submesh.indexCount > indicesCount ||
                    submesh.verticesStart + submesh.verticesCount > vertexCount
                ) {
                    outputError = 'submeshes';
                }
            });
        }

        if (!outputError) {
            const c = vertexData.indices.length;
            let maxVertex = -1;
            for (let i = 0; i < c; i++) {
                maxVertex = Math.max(maxVertex, vertexData.indices[i]);
            }
            if (maxVertex > vertexData.positions.length / 3) {
                outputError = 'vertices';
            }
        }
    }

    return outputError;
}

export function getPolygonNormal(_points: Vector3[] | number[] | Float32Array) {
    const normal = Vector3.Zero();
    if (!_points) {
        return normal;
    }

    if (typeof _points[0] === 'number') {
        const points = _points as number[] | Float32Array;
        if (points.length < 9) {
            return normal;
        }

        const p1 = Vector3.Zero();
        const p2 = Vector3.Zero();

        let i = 0;
        while (p1.length() === 0 && points.length > i + 5) {
            p1.set(points[i + 3], points[i + 4], points[i + 5]);
            p1.subtractFromFloatsToRef(points[i], points[i + 1], points[i + 2], p1);
            i += 3;
        }

        p2.set(points[i], points[i + 1], points[i + 2]);
        while (normal.length() === 0 && points.length > i + 3) {
            p2.set(points[i + 3], points[i + 4], points[i + 5]);
            p2.subtractFromFloatsToRef(points[i], points[i + 1], points[i + 2], p2);
            Vector3.CrossToRef(p1, p2, normal);
            i += 3;
        }
    } else {
        const points = _points as Vector3[];
        if (points.length < 3) {
            return normal;
        }
        const p1 = Vector3.Zero();
        const p2 = Vector3.Zero();

        let i = 0;
        while (p1.length() === 0 && points.length > i + 1) {
            p1.copyFrom(points[i + 1]);
            p1.subtractInPlace(points[i]);
            i++;
        }

        while (normal.length() === 0 && points.length > i + 1) {
            p2.copyFrom(points[i + 1]);
            p2.subtractInPlace(points[i]);
            Vector3.CrossToRef(p1, p2, normal);
            i++;
        }
    }

    return normal.normalize();
}